import type { ReactNode } from 'react'
import { useCallback, useState } from 'react'
import { ArrowRight, ArrowUpRight } from 'lucide-react'
import { Link, useParams } from 'react-router-dom'

import { AppShell } from '@/components/px/AppShell'
import { DensityNote } from '@/components/px/DensityNote'
import { SectionLabel } from '@/components/px/SectionLabel'
import { StatusPill, type StatusTone } from '@/components/px/StatusPill'
import { libraryNav } from '@/components/px/nav'
import { ConceptBadge } from '@/components/px/ConceptBadge'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  ACTIONS,
  ACTIONS_BLOCK,
  ANCHORS,
  COMPARE,
  CONTENTS,
  CONTENTS_TABLE,
  DENSITY_NOTE,
  HEADER,
  HREFS,
  IMPROVEMENTS,
  IMPROVEMENTS_PANEL,
  MAINTAINER_LINK,
  TEMPLATE_DETAIL_PAGE,
  USED_BY,
  USED_BY_TABLE,
  packById,
  type Fragment,
  type Outcome,
} from '@/demo/content/templateDetail'

/**
 * 2.4 Template detail — one reusable pack, read from the library.
 *
 * The argument the surface makes is the quote in the mint panel: a defect
 * found in one country becomes a regression test everywhere. So the page is
 * ordered as an answer to "what would installing this put into my project,
 * and what have other projects already sent back into it" — contents first,
 * then who is running it, then what came back, and only then the actions.
 *
 * Colour follows the locked contract: mint (bg-accent) is what PX proposes —
 * the returned improvements and the one decision-sized action — while the
 * two registers stay on card. Nothing here is a literal: every figure, name,
 * sentence and route comes from `@/demo/content/templateDetail`, which reads
 * the seed in `@/demo/data`.
 */

/** Locked pill semantics for this surface (StatusPill is never restyled). */
const OUTCOME_TONE: Record<Outcome, StatusTone> = {
  'In use': 'done',
  Partial: 'review',
  Delivered: 'neutral',
}

/** Ids for aria-describedby — content keys are prose, so they are slugified. */
const slug = (key: string): string =>
  key
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')

/** A sentence whose link targets live in the content module. */
const sentenceNodes = (parts: readonly Fragment[]): ReactNode[] =>
  parts.map((part, i) =>
    part.to ? (
      <Link
        key={`${part.text}-${i}`}
        to={part.to}
        className="font-medium text-accent-foreground hover:underline"
      >
        {part.text}
      </Link>
    ) : (
      <span key={`${part.text}-${i}`}>{part.text}</span>
    )
  )

function Sentence({ parts, className }: { parts: readonly Fragment[]; className?: string }) {
  return <p className={className}>{sentenceNodes(parts)}</p>
}

/** The pack asset each improvement landed in, named as the contents table names it. */
const landingAsset = (improvementKey: string): string | undefined =>
  COMPARE.rows.find((row) => row.key === improvementKey)?.asset

export default function TemplateDetailPage() {
  const { templateId } = useParams<{ templateId: string }>()
  // Only the retail rollout pack is authored; `packById` resolves any other
  // id to it, so a stray link from the library or a project never dead-ends.
  const pack = packById(templateId)

  const [compareOpen, setCompareOpen] = useState(false)
  const onCompareToggled = useCallback(() => setCompareOpen((open) => !open), [])

  return (
    <AppShell
      moduleLabel={TEMPLATE_DETAIL_PAGE.moduleLabel}
      breadcrumb={`${TEMPLATE_DETAIL_PAGE.breadcrumbRoot}${TEMPLATE_DETAIL_PAGE.breadcrumbSeparator}${pack.name}`}
      contextPill={TEMPLATE_DETAIL_PAGE.contextPill}
      nav={libraryNav}
    >
      {/* PX-W6.6 (AC-1.6): this surface renders demo content and had no
          badge. Never hide a surface — badge it. */}
      <div className="mb-3">
        <ConceptBadge />
      </div>
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <header className="max-w-4xl">
        <Link
          to={HREFS.templates}
          className="text-xs font-medium text-accent-foreground hover:underline"
        >
          {HEADER.backLabel}
        </Link>
        <div className="mt-1 flex flex-wrap items-center gap-3">
          <h1 className="font-serif text-2xl font-semibold text-foreground">{HEADER.title}</h1>
          <StatusPill tone="done">{HEADER.reusePill}</StatusPill>
          <StatusPill tone="neutral">{HEADER.savesPill}</StatusPill>
        </div>
        <p className="mt-1 text-[13px] text-ink-soft">
          {HEADER.lineBefore}
          <Link
            to={MAINTAINER_LINK.href}
            className="font-medium text-accent-foreground hover:underline"
          >
            {MAINTAINER_LINK.name}
          </Link>
          {HEADER.lineAfter}
        </p>
        <p className="mt-2 text-sm text-ink-soft">{TEMPLATE_DETAIL_PAGE.standfirst}</p>
      </header>

      <div className="mt-6 grid gap-4 xl:grid-cols-2">
        {/* ── 1 · Contents ──────────────────────────────────────────────── */}
        <section
          id={ANCHORS.contents}
          aria-labelledby="contents-heading"
          className="rounded-lg border border-border bg-card"
        >
          <div className="border-b border-border px-4 py-3">
            <SectionLabel>{CONTENTS_TABLE.label}</SectionLabel>
            <h2 id="contents-heading" className="mt-0.5 font-serif text-lg font-semibold text-foreground">
              {CONTENTS_TABLE.heading}
            </h2>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="px-4">{CONTENTS_TABLE.columns.asset}</TableHead>
                <TableHead>{CONTENTS_TABLE.columns.type}</TableHead>
                <TableHead className="px-4">{CONTENTS_TABLE.columns.phase}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {CONTENTS.map((asset) => (
                <TableRow key={asset.key}>
                  <TableCell className="max-w-sm px-4 whitespace-normal align-top">
                    <span className="text-[13px] font-medium text-foreground">{asset.asset}</span>
                    {asset.note && (
                      <Sentence parts={asset.note} className="mt-0.5 text-xs text-muted-foreground" />
                    )}
                  </TableCell>
                  <TableCell className="align-top text-[13px] text-ink-soft">{asset.type}</TableCell>
                  <TableCell className="px-4 align-top text-[13px] text-ink-soft">
                    {asset.phaseLabel}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <p className="border-t border-border px-4 py-2.5 text-xs text-muted-foreground">
            {CONTENTS_TABLE.caption}
          </p>
        </section>

        {/* ── 2 · Used by ───────────────────────────────────────────────── */}
        <section
          id={ANCHORS.usedBy}
          aria-labelledby="used-by-heading"
          className="rounded-lg border border-border bg-card"
        >
          <div className="border-b border-border px-4 py-3">
            <SectionLabel>{USED_BY_TABLE.label}</SectionLabel>
            <h2 id="used-by-heading" className="mt-0.5 font-serif text-lg font-semibold text-foreground">
              {USED_BY_TABLE.heading}
            </h2>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="px-4">{USED_BY_TABLE.columns.project}</TableHead>
                <TableHead>{USED_BY_TABLE.columns.office}</TableHead>
                <TableHead className="px-4">{USED_BY_TABLE.columns.outcome}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {USED_BY.map((row) => (
                <TableRow key={row.key}>
                  <TableCell className="max-w-xs px-4 whitespace-normal align-top">
                    <Link
                      to={row.href}
                      className="inline-flex items-center gap-1 text-[13px] font-medium text-foreground hover:underline"
                    >
                      {row.client}
                      <ArrowUpRight className="h-3 w-3 text-accent-foreground" aria-hidden />
                    </Link>
                    <span className="mt-0.5 block text-xs text-muted-foreground">{row.meta}</span>
                  </TableCell>
                  <TableCell className="align-top text-[13px] text-ink-soft">{row.office}</TableCell>
                  <TableCell className="max-w-xs px-4 whitespace-normal align-top">
                    <StatusPill tone={OUTCOME_TONE[row.outcome]}>{row.outcome}</StatusPill>
                    <span className="mt-0.5 block text-xs text-muted-foreground">{row.outcomeNote}</span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Sentence
            parts={USED_BY_TABLE.caption}
            className="border-t border-border px-4 py-2.5 text-xs text-muted-foreground"
          />
        </section>
      </div>

      {/* ── 3 · Improvements returned (mint — what came back) ───────────── */}
      <section
        id={ANCHORS.improvements}
        aria-labelledby="improvements-heading"
        className="mt-4 rounded-lg border border-border bg-accent px-4 py-4"
      >
        <SectionLabel>{IMPROVEMENTS_PANEL.label}</SectionLabel>
        <h2 id="improvements-heading" className="mt-0.5 font-serif text-lg font-semibold text-foreground">
          {IMPROVEMENTS_PANEL.heading}
        </h2>
        <blockquote className="mt-2 border-l-2 border-mint pl-3 font-serif text-base text-foreground">
          {IMPROVEMENTS_PANEL.quote}
        </blockquote>

        <Table className="mt-3">
          <TableHeader>
            <TableRow>
              <TableHead>{IMPROVEMENTS_PANEL.columns.version}</TableHead>
              <TableHead>{IMPROVEMENTS_PANEL.columns.change}</TableHead>
              <TableHead>{IMPROVEMENTS_PANEL.columns.source}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {IMPROVEMENTS.map((improvement) => {
              const asset = landingAsset(improvement.key)
              return (
                <TableRow key={improvement.key}>
                  <TableCell className="align-top">
                    <StatusPill tone="decided" className="font-mono">
                      {improvement.version}
                    </StatusPill>
                  </TableCell>
                  <TableCell className="max-w-2xl whitespace-normal align-top">
                    <span className="text-[13px] font-medium text-foreground">{improvement.change}</span>
                    {asset && (
                      <span className="mt-0.5 block text-xs text-muted-foreground">
                        {IMPROVEMENTS_PANEL.landsLabel} {asset}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="max-w-xs whitespace-normal align-top">
                    <Link
                      to={improvement.sourceHref}
                      className="text-[13px] font-medium text-accent-foreground hover:underline"
                    >
                      {improvement.sourceClient}
                    </Link>
                    <span className="mt-0.5 block text-xs text-muted-foreground">
                      {improvement.evidence}
                    </span>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>

        <Sentence
          parts={IMPROVEMENTS_PANEL.closing}
          className="mt-3 border-t border-mint/30 pt-2.5 text-xs text-muted-foreground"
        />
      </section>

      {/* ── 4 · Actions, each stating its consequence ───────────────────── */}
      <section id={ANCHORS.actions} aria-labelledby="actions-heading" className="mt-6">
        <SectionLabel>{ACTIONS_BLOCK.label}</SectionLabel>
        <h2 id="actions-heading" className="mt-0.5 font-serif text-lg font-semibold text-foreground">
          {ACTIONS_BLOCK.heading}
        </h2>

        <div className="mt-3 flex flex-wrap items-start gap-4">
          {ACTIONS.map((action) => {
            const noteId = `action-${slug(action.key)}`
            return action.kind === 'link' ? (
              <div key={action.key} className="max-w-xs">
                <Button
                  asChild
                  variant={action.emphasis === 'decision' ? 'accent' : 'outline'}
                  size={action.emphasis === 'decision' ? 'lg' : 'default'}
                >
                  <Link to={action.to} aria-describedby={noteId}>
                    {action.label}
                    <ArrowRight className="h-3.5 w-3.5" aria-hidden />
                  </Link>
                </Button>
                <p id={noteId} className="mt-2 text-xs leading-relaxed text-muted-foreground">
                  {action.consequence}
                </p>
              </div>
            ) : (
              <div key={action.key} className="max-w-xs">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onCompareToggled}
                  aria-expanded={compareOpen}
                  aria-controls={ANCHORS.compare}
                  aria-describedby={noteId}
                >
                  {compareOpen ? COMPARE.closeLabel : action.label}
                </Button>
                <p id={noteId} className="mt-2 text-xs leading-relaxed text-muted-foreground">
                  {action.consequence}
                </p>
              </div>
            )
          })}
        </div>

        {/* ── 5 · The v3 → v4 diff, opened in place ────────────────────── */}
        <div
          id={ANCHORS.compare}
          hidden={!compareOpen}
          className="mt-4 rounded-lg border border-border bg-card"
        >
          <div className="border-b border-border px-4 py-3">
            <SectionLabel>{COMPARE.label}</SectionLabel>
            <h3 className="mt-0.5 font-serif text-lg font-semibold text-foreground">
              {COMPARE.heading}
            </h3>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="px-4">{COMPARE.columns.asset}</TableHead>
                <TableHead>{COMPARE.columns.before}</TableHead>
                <TableHead>{COMPARE.columns.after}</TableHead>
                <TableHead className="px-4">{COMPARE.columns.source}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {COMPARE.rows.map((row) => (
                <TableRow key={row.key}>
                  <TableCell className="px-4 whitespace-normal align-top text-[13px] font-medium text-foreground">
                    {row.asset}
                  </TableCell>
                  <TableCell className="max-w-xs whitespace-normal align-top text-[13px] text-muted-foreground">
                    {row.before}
                  </TableCell>
                  <TableCell className="max-w-xs whitespace-normal align-top text-[13px] text-foreground">
                    {row.after}
                  </TableCell>
                  <TableCell className="px-4 whitespace-normal align-top text-[13px] text-ink-soft">
                    {row.source}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <p className="border-t border-border px-4 py-2.5 text-xs text-muted-foreground">
            {COMPARE.unchanged}
          </p>
        </div>
      </section>

      <DensityNote>{sentenceNodes(DENSITY_NOTE)}</DensityNote>
    </AppShell>
  )
}
